import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const USER_PANEL_API_URL = process.env.REACT_APP_USER_PANEL_API_URL
    || apiUrl("user_panel.php");
const PROFILE_API_URL = process.env.REACT_APP_PROFILE_API_URL
    || apiUrl("profile.php");

export default function UserPanelPage({ user, text, onBack, onUserUpdated }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [reload, setReload] = useState(0);
    const [profileName, setProfileName] = useState(user.name);
    const [facebookUrl, setFacebookUrl] = useState("");
    const [instagramUrl, setInstagramUrl] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [profileFile, setProfileFile] = useState(null);
    const [profileError, setProfileError] = useState("");
    const [profileStatus, setProfileStatus] = useState("");
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    const loadPanel = useCallback(async (signal) => {
        setIsLoading(true);
        setError("");
        try {
            const response = await fetch(USER_PANEL_API_URL, { signal });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `User panel API returned HTTP ${response.status}`);
            }
            setData(result);
            setProfileName(result.profile.name);
            setFacebookUrl(result.profile.facebookUrl || "");
            setInstagramUrl(result.profile.instagramUrl || "");
            onUserUpdated((currentUser) => ({
                ...currentUser,
                ...result.profile
            }));
        } catch (requestError) {
            if (requestError.name !== "AbortError") {
                console.error("Failed to load user panel:", requestError);
                setError(text.userPanelLoadError);
            }
        } finally {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [onUserUpdated, text]);

    useEffect(() => {
        const controller = new AbortController();
        loadPanel(controller.signal);
        return () => controller.abort();
    }, [loadPanel, reload]);

    const handleProfileSubmit = async (event) => {
        event.preventDefault();
        setProfileError("");
        setProfileStatus("");
        setIsSavingProfile(true);

        const formData = new FormData();
        formData.append("name", profileName);
        formData.append("facebookUrl", facebookUrl);
        formData.append("instagramUrl", instagramUrl);
        if (currentPassword || newPassword) {
            formData.append("currentPassword", currentPassword);
            formData.append("newPassword", newPassword);
        }
        if (profileFile) {
            formData.append("profileImage", profileFile);
        }

        try {
            const response = await fetch(PROFILE_API_URL, {
                method: "POST",
                body: formData
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `Profile API returned HTTP ${response.status}`);
            }
            onUserUpdated(result.user);
            setData((currentData) => ({
                ...currentData,
                profile: {
                    ...currentData.profile,
                    name: result.user.name,
                    profileImage: result.user.profileImage,
                    facebookUrl: result.user.facebookUrl || "",
                    instagramUrl: result.user.instagramUrl || ""
                }
            }));
            setProfileName(result.user.name);
            setFacebookUrl(result.user.facebookUrl || "");
            setInstagramUrl(result.user.instagramUrl || "");
            setCurrentPassword("");
            setNewPassword("");
            setProfileFile(null);
            event.target.reset();
            setProfileStatus(text.profileUpdated);
        } catch (requestError) {
            console.error("Failed to update user profile:", requestError);
            setProfileError(
                text.profileErrors[requestError.message] || text.profileUpdateError
            );
        } finally {
            setIsSavingProfile(false);
        }
    };

    return (
        <main className="user-panel">
            <header className="user-panel-header">
                <div>
                    <span className="quiz-label">{text.userPanelLabel}</span>
                    <h1>{text.userPanelTitle}</h1>
                    <p>{text.userPanelWelcome} {data?.profile.name || user.name}</p>
                </div>
                <button className="quiz-secondary-btn" type="button" onClick={onBack}>
                    {text.backToQuizzes}
                </button>
            </header>

            {error && (
                <div className="quiz-empty-state" role="alert">
                    <p>{error}</p>
                    <button
                        className="quiz-btn"
                        type="button"
                        onClick={() => setReload((version) => version + 1)}
                    >
                        {text.retry}
                    </button>
                </div>
            )}

            {isLoading ? (
                <p className="quiz-empty-state" role="status">{text.userPanelLoading}</p>
            ) : data && (
                <>
                    <section className="user-profile-card">
                        <h2>{text.profileSettings}</h2>
                        <form className="user-profile-form" onSubmit={handleProfileSubmit}>
                            <div className="profile-image-row">
                                {data.profile.profileImage ? (
                                    <img
                                        className="profile-avatar"
                                        src={data.profile.profileImage}
                                        alt={text.profileImageAlt}
                                    />
                                ) : (
                                    <div className="profile-avatar profile-avatar-placeholder"
                                        aria-label={text.noProfileImage}>
                                        {data.profile.name.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <label className="auth-field">
                                    <span>{text.profileImageLabel}</span>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={(event) => {
                                            const file = event.target.files?.[0] || null;
                                            if (file && file.size > 2 * 1024 * 1024) {
                                                setProfileFile(null);
                                                setProfileError(text.profileErrors.invalid_image);
                                                event.target.value = "";
                                                return;
                                            }
                                            setProfileError("");
                                            setProfileFile(file);
                                        }}
                                    />
                                    <small>{text.profileImageHint}</small>
                                </label>
                            </div>
                            <label className="auth-field">
                                <span>{text.nameLabel}</span>
                                <input
                                    type="text"
                                    maxLength="100"
                                    required
                                    value={profileName}
                                    onChange={(event) => setProfileName(event.target.value)}
                                />
                            </label>
                            <div className="profile-social-fields">
                                <label className="auth-field">
                                    <span>{text.facebookProfileLabel}</span>
                                    <input
                                        type="url"
                                        inputMode="url"
                                        maxLength="500"
                                        placeholder="https://www.facebook.com/..."
                                        value={facebookUrl}
                                        onChange={(event) => setFacebookUrl(event.target.value)}
                                    />
                                </label>
                                <label className="auth-field">
                                    <span>{text.instagramProfileLabel}</span>
                                    <input
                                        type="url"
                                        inputMode="url"
                                        maxLength="500"
                                        placeholder="https://www.instagram.com/..."
                                        value={instagramUrl}
                                        onChange={(event) => setInstagramUrl(event.target.value)}
                                    />
                                </label>
                            </div>
                            <div className="profile-password-fields">
                                <label className="auth-field">
                                    <span>{text.currentPasswordLabel}</span>
                                    <input
                                        type="password"
                                        autoComplete="current-password"
                                        value={currentPassword}
                                        onChange={(event) => setCurrentPassword(event.target.value)}
                                    />
                                </label>
                                <label className="auth-field">
                                    <span>{text.newPasswordLabel}</span>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        minLength="8"
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                    />
                                    <small>{text.passwordHint}</small>
                                </label>
                            </div>
                            {profileError && <p className="auth-error" role="alert">{profileError}</p>}
                            {profileStatus && <p className="profile-success" role="status">{profileStatus}</p>}
                            <button className="quiz-btn" type="submit" disabled={isSavingProfile}>
                                {isSavingProfile ? text.profileSaving : text.saveProfile}
                            </button>
                        </form>
                    </section>

                    <section className="user-panel-stats" aria-label={text.userStats}>
                        <article className="user-stat-card">
                            <span>{text.completedQuizzes}</span>
                            <strong>{data.summary.completedCount}</strong>
                        </article>
                        <article className="user-stat-card">
                            <span>{text.averageScore}</span>
                            <strong>{data.summary.averagePercent}%</strong>
                        </article>
                        <article className="user-stat-card">
                            <span>{text.perfectScores}</span>
                            <strong>{data.summary.perfectCount}</strong>
                        </article>
                    </section>

                    <section className="user-attempts">
                        <h2>{text.startedQuizzes}</h2>
                        {data.startedAttempts.length === 0 ? (
                            <p className="quiz-empty-state">{text.noStartedQuizzes}</p>
                        ) : (
                            <div className="user-attempt-list">
                                {data.startedAttempts.map((attempt) => (
                                    <article className="user-attempt" key={attempt.id}>
                                        <div>
                                            <h3>{attempt.quizTitle}</h3>
                                            <time dateTime={attempt.startedAt}>
                                                {text.startedAt}{" "}
                                                {new Date(attempt.startedAt.replace(" ", "T"))
                                                    .toLocaleString()}
                                            </time>
                                        </div>
                                        <strong>{text.inProgress}</strong>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="user-attempts">
                        <h2>{text.quizHistory}</h2>
                        {data.attempts.length === 0 ? (
                            <p className="quiz-empty-state">{text.noCompletedQuizzes}</p>
                        ) : (
                            <div className="user-attempt-list">
                                {data.attempts.map((attempt) => (
                                    <article className="user-attempt" key={attempt.id}>
                                        <div>
                                            <h3>{attempt.quizTitle}</h3>
                                            <time dateTime={attempt.completedAt}>
                                                {new Date(attempt.completedAt.replace(" ", "T"))
                                                    .toLocaleString()}
                                            </time>
                                        </div>
                                        <strong>
                                            {attempt.score} / {attempt.totalQuestions}
                                            {" · "}
                                            {Math.round(
                                                (attempt.score / attempt.totalQuestions) * 100
                                            )}%
                                        </strong>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}
        </main>
    );
}
